import React, {useCallback, useMemo} from "react";
import {
    AvatarGroup,
    Badge,
    ButtonGroup,
    IconButton,
    Link,
    Paper,
    Stack,
    SvgIconTypeMap,
    Tooltip,
    Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/AddCircle";
import AsteriskIcon from "@mui/icons-material/Emergency";
import TraktorIcon from "@mui/icons-material/Agriculture";
import DeleteIcon from "@mui/icons-material/Delete";
import {INeighbourhood, SeatingPlan} from "../../api/Model";
import RelativeTime from "../../components/relative-time/RelativeTime";
import {Link as RouterLink} from 'react-router-dom';
import {AppPages, usePageUrl} from "../../app-layout/AppPages";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import {useNeighbourhoodStore} from "../../api/NeighbourhoodStore";
import {associateBy, groupBy, mapValues} from "../../util/MapUtil";
import {OverridableComponent} from "@mui/material/OverridableComponent";
import {SeatingPlanModel} from "../seating-plan-page/SeatingPlanModel";
import {useRemoteValue} from "../../util/RemoteValue";


export function SeatingPlanPane({seatingPlan, onEdit, onDelete}: {
    seatingPlan: SeatingPlan,
    onEdit?: (plan: SeatingPlan) => void,
    onDelete?: (plan: SeatingPlan) => void,
}) {
    const pageUrl = usePageUrl();
    const handleEdit = useCallback(() => onEdit?.(seatingPlan), [onEdit, seatingPlan]);
    const handleDelete = useCallback(() => onDelete?.(seatingPlan), [onDelete, seatingPlan]);
    const movedEmployees = useMemo(() => {
        const employeeId2employee = new Map(seatingPlan.moves.map(move => [move.employee.id, move.employee]));
        return Array.from(employeeId2employee.values());
    }, [seatingPlan.moves]);

    const neighbourhoodStore = useNeighbourhoodStore();
    const neighbourhoods = useRemoteValue(neighbourhoodStore.remoteValue, []);
    const modifiedHoods = useMemo(() => {

        const hoodKey2Moves = mapValues(
            groupBy(seatingPlan.moves, move => SeatingPlanModel.hoodRefKey(move.neighbourhood)),
            moves => ({
                joined: moves.filter(move => move.operation === 'add').length,
                left: moves.filter(move => move.operation === 'remove').length,
            }),
        );

        const neighbourhoodUpdates = associateBy(
            seatingPlan.neighbourhoodUpdates,
            update => update.neighbourhoodId,
        );

        return [
            ...seatingPlan.newNeighbourhoods
                .map(hood => {
                    const hoodMoves = hoodKey2Moves.get(hood.key);
                    return {
                        hood,
                        icon: AddIcon,
                        title: hoodMoves
                            ? `Create new neighbourhood with ${hoodMoves.joined} employees`
                            : 'Create new neighbourhood',
                    };
                }),
            ...neighbourhoods
                .map(hood => {
                    const hoodUpdate = neighbourhoodUpdates.get(hood.id);
                    const patchedHood = hoodUpdate ? {...hood, ...hoodUpdate} : hood;
                    const hoodMoves = hoodKey2Moves.get(hood.key);
                    if (hoodUpdate || hoodMoves) {
                        return {
                            hood: patchedHood,
                            icon: hoodUpdate ? AsteriskIcon : TraktorIcon,
                            title: hoodMoves && hoodUpdate
                                ? `Update neighbourhood name, notes, or color. Add ${hoodMoves.joined} employees, remove ${hoodMoves.left} employees`
                                : hoodMoves
                                    ? `Add ${hoodMoves.joined} employees, remove ${hoodMoves.left} employees`
                                    : 'Update neighbourhood name, notes, or color',
                        };
                    } else {
                        return null;
                    }
                })
                .filter(hood => hood !== null)
        ] as { hood: INeighbourhood, icon: OverridableComponent<SvgIconTypeMap>, title: string }[];
    }, [seatingPlan, neighbourhoods]);

    return (
        <Stack component={Paper}
               padding={2}
               direction="column"
               alignItems="baseline"
        >
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" width="100%">
                <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="h6">
                        <Link component={RouterLink}
                              to={pageUrl(AppPages.seatingPlan, {planId: seatingPlan.id})}
                              underline="hover">{seatingPlan.name}</Link>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Created <RelativeTime date={new Date(seatingPlan.createdAt)}/> by {seatingPlan.createdBy}.
                        Updated <RelativeTime date={new Date(seatingPlan.updatedAt)}/> by {seatingPlan.updatedBy}.
                    </Typography>
                </Stack>
                {(onEdit || onDelete) && (
                    <ButtonGroup>
                        {onEdit && (
                            <Tooltip title="Edit">
                                <IconButton color="primary" onClick={handleEdit}>
                                    <EditIcon/>
                                </IconButton>
                            </Tooltip>
                        )}
                        {onDelete && (
                            <Tooltip title="Delete">
                                <IconButton color="error" onClick={handleDelete}>
                                    <DeleteIcon/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </ButtonGroup>
                )}
            </Stack>

            {seatingPlan.description && (
                <Typography variant="body2" color="text.secondary">{seatingPlan.description}</Typography>
            )}

            {modifiedHoods.length > 0 && (
                <Stack direction="row" flexWrap='wrap' sx={{gap: 3}} mt={2} alignItems="center" width="100%">
                    {modifiedHoods.map(({hood, icon, title}) => (
                        <Stack key={hood.id} direction="row" spacing={1}>
                            <Tooltip title={title}>
                                <Badge
                                    badgeContent={React.createElement(icon, {sx: {width: 12, height: 12}})}
                                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                >
                                    <NeighbourhoodColorIcon neighbourhood={hood}/>
                                </Badge>
                            </Tooltip>

                            <Typography variant="body1" color="text.secondary">{hood.name}</Typography>
                        </Stack>
                    ))}
                </Stack>
            )}

            {movedEmployees.length > 0 && (
                <AvatarGroup sx={{mt: 2}} max={7}>
                    {movedEmployees.map(employee => (
                        <EmployeeAvatar key={employee.id} employee={employee} showNameTooltip/>
                    ))}
                </AvatarGroup>
            )}
        </Stack>
    );
}
